import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Filter } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';

interface WordCloudModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: string;
  filename: string;
  speakerLabels?: any;
}

interface Word {
  text: string;
  count: number;
  size: number;
}

const WordCloudModal: React.FC<WordCloudModalProps> = ({
  isOpen,
  onClose,
  transcript,
  filename,
  speakerLabels
}) => {
  const { isDarkMode } = useDarkMode();
  const [minWordLength, setMinWordLength] = useState(4);
  const [maxWords, setMaxWords] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  // Common stop words to filter out
  const stopWords = new Set([
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i',
    'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at',
    'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she',
    'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their',
    'what', 'so', 'up', 'out', 'if', 'about', 'who', 'get', 'which', 'go',
    'me', 'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know',
    'take', 'people', 'into', 'year', 'your', 'good', 'some', 'could', 'them',
    'see', 'other', 'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over',
    'think', 'also', 'back', 'after', 'use', 'two', 'how', 'our', 'work',
    'first', 'well', 'way', 'even', 'new', 'want', 'because', 'any', 'these',
    'give', 'day', 'most', 'us', 'is', 'was', 'are', 'been', 'has', 'had',
    'were', 'said', 'did', 'get', 'may', 'very', 'made', 'find', 'where',
    'much', 'too', 'very', 'still', 'being', 'going', 'why', 'yeah', 'yes',
    'okay', 'ok', 'um', 'uh', 'ah', 'oh', 'really', 'right', 'mean', 'thing',
    'things', 'something', 'anything', 'nothing', 'everything', 'somewhere',
    'here', 'there', 'maybe', 'probably', 'actually', 'basically', 'literally'
  ]);

  const words = useMemo(() => {
    // Get all speaker names to exclude
    const speakerNames = new Set<string>();
    if (speakerLabels) {
      // Add all speaker labels
      Object.values(speakerLabels).forEach((name: any) => {
        if (typeof name === 'string') {
          // Add full name and individual words
          speakerNames.add(name.toLowerCase());
          name.split(' ').forEach((part: string) => {
            if (part.length > 0) {
              speakerNames.add(part.toLowerCase());
            }
          });
        }
      });
      // Also add generic speaker patterns
      for (let i = 0; i <= 20; i++) {
        speakerNames.add(`speaker${i}`);
        speakerNames.add(`speaker`);
      }
    }
    
    // Clean and tokenize the transcript
    const cleanedText = transcript
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ');
    
    const wordArray = cleanedText.split(' ').filter(word => 
      word.length >= minWordLength && 
      !stopWords.has(word) && 
      !speakerNames.has(word)
    );

    // Count word frequencies
    const wordCount: Record<string, number> = {};
    wordArray.forEach(word => {
      wordCount[word] = (wordCount[word] || 0) + 1;
    });

    // Convert to array and sort by count
    const sortedWords = Object.entries(wordCount)
      .map(([text, count]) => ({ text, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, maxWords);

    // Calculate sizes based on frequency
    if (sortedWords.length === 0) return [];
    
    const maxCount = sortedWords[0].count;
    const minCount = sortedWords[sortedWords.length - 1].count;
    const range = maxCount - minCount || 1;

    return sortedWords.map(word => ({
      ...word,
      size: 14 + ((word.count - minCount) / range) * 36 // Size range: 14px to 50px
    }));
  }, [transcript, minWordLength, maxWords, speakerLabels]);

  // Generate random positions for words
  const [wordPositions, setWordPositions] = useState<Array<{ x: number; y: number; rotation: number }>>([]);
  
  useEffect(() => {
    const positions = words.map(() => ({
      x: Math.random() * 80 + 10, // 10% to 90% of container width
      y: Math.random() * 80 + 10, // 10% to 90% of container height
      rotation: Math.random() * 30 - 15 // -15 to 15 degrees
    }));
    setWordPositions(positions);
  }, [words]);

  const getWordColor = (index: number) => {
    const colors = isDarkMode
      ? ['#60A5FA', '#34D399', '#F59E0B', '#EF4444', '#A78BFA', '#EC4899', '#10B981', '#3B82F6']
      : ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#DB2777', '#047857', '#1D4ED8'];
    return colors[index % colors.length];
  };

  const handleDownload = () => {
    const svgElement = document.getElementById('word-cloud-svg');
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\.[^/.]+$/, '')}_wordcloud.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Word Cloud
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                title="Filter options"
              >
                <Filter size={20} />
              </button>
              <button
                onClick={handleDownload}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                title="Download as SVG"
              >
                <Download size={20} />
              </button>
              <button
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'hover:bg-gray-700 text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center space-x-4">
                <div>
                  <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Min Word Length
                  </label>
                  <input
                    type="number"
                    value={minWordLength}
                    onChange={(e) => setMinWordLength(Math.max(1, parseInt(e.target.value) || 1))}
                    className={`ml-2 w-16 px-2 py-1 rounded border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    min="1"
                    max="10"
                  />
                </div>
                <div>
                  <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Max Words
                  </label>
                  <input
                    type="number"
                    value={maxWords}
                    onChange={(e) => setMaxWords(Math.max(10, parseInt(e.target.value) || 10))}
                    className={`ml-2 w-20 px-2 py-1 rounded border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    min="10"
                    max="200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Word Cloud */}
          <div className="flex-1 p-4 overflow-hidden">
            <div className={`w-full h-full rounded-lg ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} relative`}>
              <svg
                id="word-cloud-svg"
                width="100%"
                height="100%"
                viewBox="0 0 800 600"
                preserveAspectRatio="xMidYMid meet"
              >
                {words.map((word, index) => {
                  const position = wordPositions[index];
                  if (!position) return null;
                  
                  return (
                    <motion.text
                      key={word.text}
                      x={`${position.x}%`}
                      y={`${position.y}%`}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.02 }}
                      style={{
                        fontSize: `${word.size}px`,
                        fill: getWordColor(index),
                        fontWeight: word.size > 30 ? 'bold' : 'normal',
                        transform: `rotate(${position.rotation}deg)`,
                        transformOrigin: 'center',
                        cursor: 'default'
                      }}
                      className="select-none"
                    >
                      {word.text}
                      <title>{`${word.text}: ${word.count} occurrences`}</title>
                    </motion.text>
                  );
                })}
              </svg>
            </div>
          </div>

          {/* Footer Stats */}
          <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between text-sm">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Total unique words: {words.length}
              </span>
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Most frequent: {words[0]?.text} ({words[0]?.count} times)
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WordCloudModal;
